// hooks/usePcakeImage.ts
import { useEffect, useRef, useState } from 'react';
import { cacheFile } from '../services/mediaCache';

export function usePcakeImage(
  chatId: string,
  messageId: string,
  signedUrl: string,
  mimeType: string,
): string {
  const [url, setUrl] = useState<string>(signedUrl);
  // Track the current blob URL via ref so cleanup never operates on stale state
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!chatId || !messageId || !signedUrl) {
      setUrl('');
      return;
    }

    let cancelled = false;

    async function resolve() {
      try {
        const cachedUrl = await cacheFile(chatId, messageId, signedUrl, mimeType);

        if (!cancelled) {
          // If we got a new blob URL, revoke the old one and track the new one
          if (cachedUrl.startsWith('blob:')) {
            if (blobUrlRef.current && blobUrlRef.current !== cachedUrl) {
              URL.revokeObjectURL(blobUrlRef.current);
            }
            blobUrlRef.current = cachedUrl;
          }
          setUrl(cachedUrl);
        }
      } catch (err) {
        console.warn('❌ usePcakeImage: Cache failed, using signed URL:', err);
        if (!cancelled) {
          setUrl(signedUrl); // Fallback to original signed URL
        }
      }
    }

    resolve();

    return () => {
      cancelled = true;
      // Do NOT revoke blob URLs here — stale closure would revoke the wrong URL.
      // Revocation is handled by the unmount effect below.
    };
  }, [chatId, messageId, signedUrl, mimeType]);

  // Revoke the blob URL only when the component truly unmounts
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  return url;
}